import React from "react";
import { Stack, PrimaryButton, Callout, TextField, DefaultButton, Link } from "office-ui-fabric-react";
import { Text } from "office-ui-fabric-react/lib/Text";
import { Icon } from "office-ui-fabric-react/lib/Icon";
import { DirectionalHint } from "office-ui-fabric-react/lib/Callout";
import { TooltipHost } from "office-ui-fabric-react/lib/components/Tooltip/TooltipHost";
import { GridItem } from "./PortalSelector";
import { withTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

interface DisallowedRecipientCardProps {
  t: TFunction,
  gridItems: GridItem[];
  onJustify: (item: GridItem, justification: string) => void;
  canOverride?: boolean;
}

const DisallowedRecipientCard: React.FC<DisallowedRecipientCardProps> = ({ t, gridItems, onJustify, canOverride }) => {
  const [currentItem, setCurrentItem] = React.useState<GridItem | null>(null);
  const [justification, setJustification] = React.useState<string>("");
  const [isCalloutVisible, setIsCalloutVisible] = React.useState<boolean>(false);
  const callOutRefs = React.useRef<Map<string, HTMLElement | null>>(new Map());

  const handleAllowClick = (item: GridItem) => {
    setCurrentItem(item);
    setIsCalloutVisible(true);
  };

  const handleJustificationSubmit = () => {
    if (currentItem) {
      onJustify(currentItem, justification);
      setIsCalloutVisible(false);
      setJustification("");
      setCurrentItem(null);
    }
  };

  const handleCloseJustification = () => {
    setIsCalloutVisible(false);
    setJustification("");
    setCurrentItem(null);
  };

  return (
    <div>
      {gridItems.map((item: GridItem, key) => (
        <Stack
          tokens={{ childrenGap: 10 }}
          styles={{
            root: {
              border: "1px solid #ddd",
              padding: 10,
              borderRadius: 4,
              marginBottom: "15px",
              opacity: item.isJustified ? 0.5 : 1,
              backgroundColor: item.isJustified ? "#f3f3f3" : "white",
            },
          }}
          key={key}
        >
          <Stack horizontal tokens={{ childrenGap: 10 }} styles={{ root: { justifyContent: 'space-between', alignItems: 'center' } }}>
            <Stack tokens={{ childrenGap: 10 }}>
              <Text variant="mediumPlus" styles={{ root: { fontWeight: "bold" } }}>
                {t('portal.disallowed_header', { count: 1 })}
              </Text>
              {item.name && (
                <Text variant="medium" styles={{ root: { fontWeight: "bold" } }}>
                  {item.name}
                </Text>
              )}
              <Text
                variant="small"
                styles={{
                  root: {
                    marginTop: item.name ? "0px!important" : undefined,
                  },
                }}
              >
                {item.email}
              </Text>
              <Text variant="mediumPlus" styles={{ root: { fontWeight: "bold" } }}>
                {t('portal.reason_header')}
              </Text>
              <Stack horizontal tokens={{ childrenGap: 5 }}>
                <Icon
                  iconName="AlertSolid"
                  styles={{ root: {
                    color: item.isJustified ? "#999" : "#0e285bff",
                    fontSize: "20px",
                    cursor: "initial"
                  } }}
                />

                {item.reason_url ? (
                    <Link 
                        href={item.reason_url}
                        target="_blank"
                        style={{ color: item.isJustified ? "#999" : "#1F69FF", textDecoration: "underline" }}
                    >
                        {item.reason}
                    </Link>
                ) : (
                    <Text variant="medium" styles={{ root: { color: "#1F69FF" } }}>
                      {item.reason}
                    </Text>
                )}
              </Stack>
            </Stack>
            {canOverride && !item.isJustified ? (
              item.can_override === false ? (
                <TooltipHost content={t("portal.override_not_allowed_tooltip")}>
                  <PrimaryButton
                    text="Allow"
                    disabled={true}
                    data-recipient={item.email}
                    styles={{
                      root: {
                        fontSize: "13px",
                        padding: "10px",
                        borderRadius: "6px",
                      },
                    }}
                  />
                </TooltipHost>
              ) : (
              <div ref={(el) => callOutRefs.current.set(item.email, el)} style={{ display: 'flex', alignItems: 'center' }}>
                <PrimaryButton
                  text="Allow"
                  data-recipient={item.email}
                  onClick={() => handleAllowClick(item)}
                  styles={{
                    root: {
                      fontSize: "13px",
                      padding: "10px",
                      borderRadius: "6px",
                      backgroundColor: "#4181FF",
                    },
                  }}
                />
                {isCalloutVisible && currentItem?.email === item.email && (
                  <Callout
                    target={callOutRefs.current.get(item.email)}
                    onDismiss={handleCloseJustification}
                    directionalHint={DirectionalHint.rightCenter}
                    isBeakVisible={true}
                    styles={{
                      root: {
                        minWidth: 300,
                        padding: "10px 15px",
                      },
                    }}
                  >
                    <Stack tokens={{ childrenGap: 8 }}>
                      <TextField
                        placeholder="Enter justification..."
                        multiline
                        borderless
                        rows={3}
                        styles={{
                          root: { fontSize: "13px" },
                          field: {
                            "::placeholder": {
                              color: "#707070",
                              fontSize: "13px",
                            },
                          },
                        }}
                        resizable={false}
                        value={justification}
                        onChange={(_, newValue) => setJustification(newValue || "")}
                      />
                      <Stack horizontal tokens={{ childrenGap: 8 }} horizontalAlign="end">
                        <DefaultButton
                          text="Cancel"
                          onClick={handleCloseJustification}
                          styles={{
                            root: {
                              fontSize: "12px",
                              padding: "8px",
                              borderRadius: "6px",
                              border: "1px solid #4181FF",
                            },
                          }}
                        />
                        <PrimaryButton
                          text="Submit"
                          onClick={handleJustificationSubmit}
                          disabled={!justification}
                          styles={{
                            root: {
                              fontSize: "12px",
                              padding: "8px",
                              borderRadius: "6px",
                              backgroundColor: "#4181FF",
                            },
                          }}
                        />
                      </Stack>
                    </Stack>
                  </Callout>
                )}
              </div>
              )
            ) : (
              canOverride && (
                <PrimaryButton
                  key={`allow-button-${item.email}-${isCalloutVisible}`}
                  text="Allowed"
                  styles={{
                      root: {
                          fontSize: "13px",
                          padding: "10px",
                          pointerEvents: "none",
                          borderRadius: "6px",
                          color: "#242424",
                          border: "1px solid #D1D1D1",
                          backgroundColor: "#fff"
                      },
                  }}
                />
              )
            )}
          </Stack>
        </Stack>
      ))}
    </div>
  );
};

export default withTranslation()(DisallowedRecipientCard);

